import {React, useEffect, useState} from 'react';
import { useNavigate, useLocation, useOutletContext } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ReactSortable } from 'react-sortablejs';

import './Dashboard.css';
import '../Animate/animate.css';
import ModalLevel from '../Modal/ModalLevel';
import ModalCategory from '../Modal/ModalCategory';
import ManageUsers from '../Supervisor/ManageUsers';
import AnnouncementModal from '../Supervisor/AnnouncementModal';
import ManageReports from '../Supervisor/ManageReports';
import Loading from '../Common/Loading';

import { isAuthenticated, isStaff, isAdmin, getCurrentUser } from '../../api/users_institutions';
import { getAllLevels, updateLevel } from '../../api/levels';
import { getAllCategories, updateCategory } from '../../api/categories';
import { getLevelAdminPermissions } from '../../api/admin_permissions';
import { getLevelDomains } from '../../api/levels';
import { getUserLevelProgresses, createUserLevelProgress } from '../../api/progresses';
import { prefetchLevelSidebarContent } from '../../api/levelContentCache';
import {
  getCachedDashboardGridContent, setCachedDashboardGridContent, invalidateDashboardGridContent,
} from '../../api/dashboardGridCache';
import i18next from 'i18next';

function LevelCard({ level, category, specializations, currentLevelDomains, levelAdminPermissions, onNavigate, isStaff, isAdmin, editorMode, setModalAction, setSelectedModalLevel, setInModalLevel, setSelectedModalCategory }) {
  const [userLevelProgress, setUserLevelProgress] = useState(null);
  const hasIcon = Boolean(level.icon_url);
  const levelDomains = currentLevelDomains.filter((domain) => Number(domain.level?.id ?? domain.level) === Number(level.id));
  const isLocked = !isAdmin && !isStaff && (specializations.length === 0 || !specializations.some((spec) =>
    levelDomains.some((domain) =>
      String(spec.target_domain || '').trim().toLowerCase() === String(domain.domain || '').trim().toLowerCase()
    )
  ));
  const userLevelPermissions = levelAdminPermissions || [];

  const fetchUserLevelProgress = async () => {
    try {
      const progresses = await getUserLevelProgresses();
      const progress = progresses.find((p) => p.level === level.id && p.user === getCurrentUser().id);
      if (progress) {
        setUserLevelProgress(progress);
      } else {
        await handleCreateUserLevelProgress();
      }
    } catch (error) {
      console.error('Error fetching user level progress:', error);
    }
  };
  const handleCreateUserLevelProgress = async () => {
    try {
      const newProgress = await createUserLevelProgress({ level: level.id, user: getCurrentUser().id, progress: 0 });
      console.log('Created new user level progress:', newProgress);
      setUserLevelProgress(newProgress);
    } catch (error) {
      console.error('Error creating user level progress:', error);
    }
  };
  useEffect(() => {
    fetchUserLevelProgress();
  }, []);

  // Warm the sidebar-content cache (topics + exams only) as soon as the user shows intent to
  // click this card, so Level.jsx's sidebar can paint instantly instead of waiting on the
  // network. onTouchStart covers mobile, where hover never fires before the tap completes.
  const handlePrefetch = () => {
    if (!isLocked) prefetchLevelSidebarContent(level.id).catch(() => {});
  };

  return (
    /*(((isStaff && userLevelPermissions.some(p => p.level === level.id)) || !isStaff) && level.is_visible) || isAdmin) && (*/
    // A superadmin sees every level, including hidden ones (they're the ones who hide them);
    // everyone else -- staff admin or educator -- sees whatever is marked visible.
    isAdmin || level.is_visible) && (
    <div
      className={`level-card${hasIcon ? ' has-image' : ''} ${isLocked ? 'locked-module' : ''}`}
      onClick={!isLocked ? () => onNavigate(level) : null}
      onMouseEnter={handlePrefetch}
      onFocus={handlePrefetch}
      onTouchStart={handlePrefetch}
      data-sortable={isAdmin && editorMode ? "true" : "false"}
      style={hasIcon ? { backgroundImage: `url(${level.icon_url})` } : undefined}
    >
      {isAdmin && editorMode && (
        <div className="admin-level-controls">
          <button className="btn-edit" onClick={(e) => {e.stopPropagation(); setModalAction('Update'); setSelectedModalCategory(category); setSelectedModalLevel(level); setInModalLevel(true); }}>
            ✎
          </button>
          <button className="btn-del" onClick={(e) => {e.stopPropagation(); setModalAction('Delete'); setSelectedModalCategory(category); setSelectedModalLevel(level); setInModalLevel(true); }}>
            ×
          </button>
        </div>
      )}
      {!hasIcon && !isLocked && (
        <h1>📚</h1>
      )}
      {isLocked && (
        <div className="locked-overlay">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="35"
              height="35"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-label="Locked module"
              role="img"
            >
              <title>Locked module</title>
              <rect x="3" y="10" width="18" height="11" rx="2.5" fill="currentColor" fillOpacity="0.12" />
              <path d="M7 10V7a5 5 0 0 1 10 0v3" />
              <circle cx="12" cy="15" r="1.25" fill="currentColor" stroke="none" />
              <path d="M12 16.25V18" />
              <path d="M5.5 10h13" opacity="0.45" />
            </svg> {/* Detailed lock icon */}
        </div>
      )}
      <h3>{level.name}</h3>
      <p style={{ fontSize: '0.9rem' }}>{level.description}</p>
      <p className="card-progress-text">{userLevelProgress ? `${userLevelProgress.progress_percentage}%` : '0%'}</p>
      <div className="card-progress-container">
        <div className="card-progress-fill" style={{width: `${userLevelProgress ? userLevelProgress.progress_percentage : 0}%`}}></div>
      </div>
    </div>
  );
}

function CategoryBlock({ category, specializations, currentLevels, setCurrentLevels, currentLevelDomains, levelAdminPermissions, onNavigate, isStaff, editorMode, setModalAction, setSelectedModalLevel, setInModalLevel, setModalCategoryAction, setSelectedModalCategory, setInModalCategory }) {
  const { t } = useTranslation();
  const categoryLevels = currentLevels.filter(level => level.category === category.id);
  const userIsAdmin = isAdmin();

  const handleLevelReorder = (newOrder) => {
    if (!newOrder || !Array.isArray(newOrder)) return;
    // Create a map of the reordered levels with their new positions
    const reorderedMap = new Map();
    newOrder.forEach((level, index) => {
      reorderedMap.set(level.id, { ...level, order: index });
    });

    // Update all levels, replacing category levels with reordered ones
    const updatedAllLevels = currentLevels.map(level => {
      if (reorderedMap.has(level.id)) {
        return reorderedMap.get(level.id);
      }
      return level;
    });

    // Sort to maintain order within category
    updatedAllLevels.sort((a, b) => {
      if (a.category === category.id && b.category === category.id) {
        return a.order - b.order;
      }
      return 0;
    });

    setCurrentLevels(updatedAllLevels);

    // Update order values in backend
    newOrder.forEach((level, index) => {
      updateLevel(level.id, { order: index }).catch(error => {
        console.error(`Error updating order for level ${level.id}:`, error);
      });
    });
  };

  return (
    // (userIsAdmin || ((category.is_visible && !userIsAdmin) && ((!isStaff && !userIsAdmin && category.audience === 'teachers') || (isStaff && category.audience === 'admins')))) && (
    // Superadmin: everything. Staff admin: every visible category, whatever its audience (the
    // "teachers" ones are read-only for them -- see the View Only badge below). Educator: only
    // the visible categories actually aimed at them.
    // NB `isAdmin` is the imported role helper, not a boolean -- use userIsAdmin here.
    (userIsAdmin || (category.is_visible && (isStaff || category.audience === 'teachers'))) && (
    <div className="category-block">
      <div className="category-header">
        {userIsAdmin && editorMode && (
          <span className="cat-handle">
            ⋮⋮
          </span>
        )}
        <div className="cat-title-group">
          <h2>{category.name} {
            category.audience === 'admins' && (
              <span className="cat-badge" style={{ background: '#fee2e2', color: 'var(--del-color)' }}>
                Admin Only
              </span>
            )
          }
          {category.audience === "teachers" && (userIsAdmin || isStaff) && (
              <span className="cat-badge" style={{ background: '#f1f5f9', color: 'var(--text-muted)' }}>
                View Only
              </span>
            )
          }
          {category.is_visible === false && (
              <span className="cat-badge" style={{ background: '#f1f5f9', color: 'var(--text-muted)' }}>
                Hidden
              </span>
            )
          }</h2>
          <p>{category.description}</p>
        </div>
        {userIsAdmin && editorMode && (
          <div className="admin-category-controls">
            <button className="btn-edit" onClick={() => { setModalCategoryAction('Update'); setSelectedModalCategory(category); setInModalCategory(true); }}>
              ✎
            </button>
            <button className="btn-del" onClick={() => { setModalCategoryAction('Delete'); setSelectedModalCategory(category); setInModalCategory(true); }}>
              ×
            </button>
          </div>
        )}
      </div>
      {userIsAdmin && editorMode ? (
        <ReactSortable
          list={categoryLevels}
          setList={handleLevelReorder}
          animation={150}
          className="level-grid"
          ghostClass="sortable-ghost"
          disabled={!isStaff}
          filter=".empty-card"
          scroll={true}
          scrollSensitivity={60}
          scrollSpeed={10}
          bubbleScroll={true}
          forceFallback={true}
          fallbackOnBody={true}
          fallbackTolerance={4}
        >
          {categoryLevels.map((level) => (
            <LevelCard
              key={level.id}
              level={level}
              category={category}
              specializations={specializations}
              currentLevelDomains={currentLevelDomains}
              levelAdminPermissions={levelAdminPermissions}
              onNavigate={onNavigate}
              isStaff={isStaff}
              isAdmin={userIsAdmin}
              editorMode={editorMode}
              setModalAction={setModalAction}
              setSelectedModalLevel={setSelectedModalLevel}
              setInModalLevel={setInModalLevel}
              setSelectedModalCategory={setSelectedModalCategory}
            />
          ))}
          <div className='level-card empty-card' onClick={() => { setModalAction('Add'); setSelectedModalCategory(category); setSelectedModalLevel(null); setInModalLevel(true); }}>
            {t('dashboard.addModule')}
          </div>
        </ReactSortable>
      ) : (
        (categoryLevels.length === 0 && userIsAdmin) || (categoryLevels.filter(item => item.is_visible).length === 0 && !userIsAdmin) ? (
          <div style={{
            color: 'var(--bg-opposite)',
            padding: '0px 18px',
            margin: '10px 0 10px',
            textAlign: 'center',
            fontSize: '0.9rem'
          }}>{t('dashboard.noModulesYet', { category: category.name })}</div>
        ) :
        (<div className="level-grid">
          {categoryLevels.map((level) => (
            <LevelCard
              key={level.id}
              level={level}
              category={category}
              specializations={specializations}
              currentLevelDomains={currentLevelDomains}
              levelAdminPermissions={levelAdminPermissions}
              onNavigate={onNavigate}
              isStaff={isStaff}
              isAdmin={userIsAdmin}
              editorMode={editorMode}
              setModalAction={setModalAction}
              setSelectedModalLevel={setSelectedModalLevel}
              setInModalLevel={setInModalLevel}
              setSelectedModalCategory={setSelectedModalCategory}
            />
          ))}
        </div>
      ))}
    </div>
  ));
}

function DashboardGrid() {

  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { editorMode, userSpecializations } = useOutletContext();
  const userIsAuthenticated = isAuthenticated()
  const userIsStaff = isStaff();
  const userIsAdmin = isAdmin();
  const currentUser = getCurrentUser();

  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && (window.matchMedia('(max-width: 760px)').matches || window.matchMedia('(max-height: 500px)').matches)
  );

  useEffect(() => {
    const widthMql = window.matchMedia('(max-width: 760px)');
    const heightMql = window.matchMedia('(max-height: 500px)');

    const handleChange = () => {
      const matches = widthMql.matches || heightMql.matches;
      setIsMobile(matches);
    };

    handleChange();
    widthMql.addEventListener('change', handleChange);
    heightMql.addEventListener('change', handleChange);
    return () => {
      widthMql.removeEventListener('change', handleChange);
      heightMql.removeEventListener('change', handleChange);
    };
  }, []);

  // Animation state - check if coming back from Level
  const [animationClass, setAnimationClass] = useState(
    location.state?.fromLevel ? 'anim-initial' : ''
  );

  const [currentLevels, setCurrentLevels] = useState([]);
  const [currentCategories, setCurrentCategories] = useState([]);
  const [currentLevelDomains, setCurrentLevelDomains] = useState([]);
  const [levelAdminPermissions, setLevelAdminPermissions] = useState([]);

  // One flag for the whole grid rather than one per fetch: every category block renders
  // its own level cards, so revealing categories before the levels land would flash
  // "No <category> modules yet" under each heading before they fill in.
  const [isLoadingGrid, setIsLoadingGrid] = useState(true);

  const [inModalLevel, setInModalLevel] = useState(false);
  const [modalAction, setModalAction] = useState('Add');
  const [selectedModalLevel, setSelectedModalLevel] = useState(null);

  const [inModalCategory, setInModalCategory] = useState(false);
  const [modalCategoryAction, setModalCategoryAction] = useState('Add');
  const [selectedModalCategory, setSelectedModalCategory] = useState(null);

  const [inModalManageAdmins, setInModalManageAdmins] = useState(false);
  const [inModalViewSuperadmins, setInModalViewSuperadmins] = useState(false);
  const [inModalSendAnnouncement, setInModalSendAnnouncement] = useState(false);
  const [inModalManageReports, setInModalManageReports] = useState(false);

  // Trigger enter animation when coming back from Level
  useEffect(() => {
    if (location.state?.fromLevel) {
      setTimeout(() => setAnimationClass('anim-enter-left'), 50);
    }
  }, [location.state]);

  const handleNavigateToLevel = (level) => {
    setAnimationClass('anim-exit-left');
    setTimeout(() => {
      // Relative navigation resolves against the matched /dashboard parent route, landing on
      // /dashboard/<level.name> -- same URL as a plain navigate('/dashboard/'+level.name) would,
      // but keeps working correctly regardless of where this route is mounted. Passing the
      // already-known level object lets Level.jsx skip its own "fetch every level and find by
      // name" lookup, and its sidebar content may already be sitting in the prefetch cache
      // populated by hovering/focusing/touching the card.
      navigate(level.name, { state: { fromDashboard: true, level } });
    }, 300);
  };

  const handleCategoryReorder = async (newOrder) => {
    setCurrentCategories(newOrder);

    // Update order values in backend
    newOrder.forEach(async (category, index) => {
      try {
        await updateCategory(category.id, { order: index });
      } catch (error) {
        console.error(`Error updating order for category ${category.id}:`, error);
      }
    });
  };

  const fetchLevels = async () => {
    // Check if token exists before making the call
    const token = localStorage.getItem('authToken');
    if (!token) {
      console.error('No auth token found in localStorage');
      return;
    }

    try {
      const levels = await getAllLevels();
      setCurrentLevels(levels);
      const levelDomains = await getLevelDomains();
      setCurrentLevelDomains(levelDomains);
      return { levels, levelDomains };
    } catch (error) {
      console.error('Error fetching levels:', error);
      return null;
    }
  };

  const fetchCategories = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      console.error('No auth token found in localStorage');
      return null;
    }

    try {
      const categories = await getAllCategories();
      setCurrentCategories(categories);
      return categories;
    } catch (error) {
      console.error('Error fetching categories:', error);
      return null;
    }
  };

  const fetchLevelAdminPermissions = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      console.error('No auth token found in localStorage');
      return null;
    }

    try {
      const permissions = await getLevelAdminPermissions();
      const filteredPermissions = permissions.filter(permission => permission.user === currentUser.id);
      setLevelAdminPermissions(filteredPermissions);
      return filteredPermissions;
    } catch (error) {
      console.error('Error fetching level user permissions:', error);
      return null;
    }
  }

  useEffect(() => {
    if (!userIsAuthenticated) return;

    let cancelled = false;

    // A valid cache paints the grid instantly with last visit's data and skips the network
    // entirely -- no fetch runs until something actually invalidates the cache (a category/level
    // create/update/delete/reorder). A cache miss falls back to the normal fetch-and-spinner path.
    const cached = getCachedDashboardGridContent();
    if (cached) {
      setCurrentLevels(cached.levels);
      setCurrentLevelDomains(cached.levelDomains);
      setCurrentCategories(cached.categories);
      setLevelAdminPermissions(cached.levelAdminPermissions);
      setIsLoadingGrid(false);
      return () => { cancelled = true; };
    }

    setIsLoadingGrid(true);

    // Each fetch swallows its own errors, so this settles even when the network is
    // down -- the grid then falls through to its normal empty state rather than
    // spinning forever.
    Promise.all([fetchLevels(), fetchCategories(), fetchLevelAdminPermissions()])
      .then(([levelsResult, categories, levelAdminPermissionsResult]) => {
        if (cancelled || !levelsResult || !categories || !levelAdminPermissionsResult) return;
        setCachedDashboardGridContent({
          levels: levelsResult.levels,
          levelDomains: levelsResult.levelDomains,
          categories,
          levelAdminPermissions: levelAdminPermissionsResult,
        });
      })
      .finally(() => { if (!cancelled) setIsLoadingGrid(false); });

    return () => { cancelled = true; };
  }, [userIsAuthenticated]);

  return (
    <>
      {userIsAuthenticated &&
        <>
          <div className={`dashboard-content dashboard-anim-container ${animationClass}`}>
            {isMobile ? (
              <div className="profile-banner-mobile"> 
                <div className="banner-right">
                  <div className="banner-pfp banner-pfp-circle" onClick={() => navigate(`/profile/${currentUser.username}`, { state: { from: location.pathname } })}>
                    {currentUser.profile_picture_url || currentUser.profile_picture ? (
                      <img src={currentUser.profile_picture_url || currentUser.profile_picture} alt="Profile" style={{ display: 'block' }} />
                    ) : (
                      <span>{currentUser.first_name?.charAt(0) || currentUser.username?.charAt(0) || '?'}</span>
                    )}
                  </div>
                </div>
                <div className="banner-left">
                  {i18next.language === 'ar' ? (
                    <h1 className="banner-greeting">
                      . <em>{currentUser.first_name || currentUser.username}</em> {t('dashboard.welcomeBack')}
                    </h1>
                  ) : (
                  <h1 className="banner-greeting">
                    {t('dashboard.welcomeBack')} <em>{currentUser.first_name || currentUser.username}</em>.
                  </h1>
                  )}
                  <p className="banner-sub">{t('dashboard.trackCertification')}</p>
                </div>
              </div>
            ) : (
              <div className="profile-banner">
                <div className="banner-left">
                  {i18next.language === 'ar' ? (
                    <h1 className="banner-greeting">
                      . <em>{currentUser.first_name || currentUser.username}</em> {t('dashboard.welcomeBack')}
                    </h1>
                  ) : (
                  <h1 className="banner-greeting">
                    {t('dashboard.welcomeBack')} <em>{currentUser.first_name || currentUser.username}</em>.
                  </h1>
                  )}
                  <p className="banner-sub">{t('dashboard.trackCertification')}</p>
                </div>
                <div className="banner-right">
                  <div className="banner-profile-info">
                    <strong>{userIsAdmin ? t('dashboard.supervisorProfile') : (userIsStaff ? t('dashboard.adminProfile') : t('dashboard.educatorProfile'))}</strong>
                    <div>{t('dashboard.accountSettings')}</div>
                  </div>
                  <div className="banner-pfp banner-pfp-circle" onClick={() => navigate(`/profile/${currentUser.username}`, { state: { from: location.pathname } })}>
                    {currentUser.profile_picture_url || currentUser.profile_picture ? (
                      <img src={currentUser.profile_picture_url || currentUser.profile_picture} alt="Profile" style={{ display: 'block' }} />
                    ) : (
                      <span>{currentUser.first_name?.charAt(0) || currentUser.username?.charAt(0) || '?'}</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div id="admin-action-bar" className="admin-action-bar">
              {userIsAdmin && editorMode && (
                <button
                  className="btn-add-category"
                  onClick={() => { setModalCategoryAction('Add'); setSelectedModalCategory(null); setInModalCategory(true); }}
                >
                  {t('dashboard.addCategory')}
                </button>
              )}
              {
                userIsAdmin && (
                  <button className="global-admin-btn" onClick={() => setInModalManageAdmins(true)}>
                    {t('dashboard.manageAdmins')}
                  </button>
                )
              }
              {
                userIsAdmin && (
                  <button className="global-superadmin-btn" onClick={() => setInModalViewSuperadmins(true)}>
                    {t('dashboard.viewSuperadmins')}
                  </button>
                )
              }
              {
                userIsAdmin && (
                  <button className="global-announcement-btn" onClick={() => setInModalSendAnnouncement(true)}>
                    {t('dashboard.sendAnnouncements')}
                  </button>
                )
              }
              {
                userIsAdmin && (
                  <button className="global-reports-btn" onClick={() => setInModalManageReports(true)}>
                    {t('dashboard.manageReports')}
                  </button>
                )
              }
            </div>
            <ReactSortable
              list={currentCategories}
              setList={handleCategoryReorder}
              animation={150}
              handle=".cat-handle"
              ghostClass="sortable-ghost"
              disabled={!userIsAdmin}
              scroll={true}
              scrollSensitivity={60}
              scrollSpeed={25}
              bubbleScroll={true}
              forceFallback={true}
              fallbackOnBody={true}
              fallbackTolerance={4}
            >
              {isLoadingGrid ? (
                <Loading variant="inline" message={t('dashboard.loadingModules')} />
              ) :
              (currentCategories.length === 0 && userIsAdmin) || (currentCategories.filter(item => item.is_visible).length === 0 && !userIsAdmin) ? (
                <div style={{
                  color: 'var(--bg-opposite)',
                  padding: '0px 18px',
                  margin: '10% 0 10%',
                  textAlign: 'center',
                  fontSize: '1.9rem'
                }}>{t('dashboard.noCategoriesYet')}</div>
              ) :
              (currentCategories.map((category) => (
                <CategoryBlock
                  key={category.id}
                  category={category}
                  specializations={userSpecializations}
                  currentLevels={currentLevels}
                  setCurrentLevels={setCurrentLevels}
                  currentLevelDomains={currentLevelDomains}
                  onNavigate={handleNavigateToLevel}
                  levelAdminPermissions={levelAdminPermissions}
                  isStaff={userIsStaff}
                  editorMode={editorMode}
                  setModalAction={setModalAction}
                  setSelectedModalLevel={setSelectedModalLevel}
                  setInModalLevel={setInModalLevel}
                  setModalCategoryAction={setModalCategoryAction}
                  setSelectedModalCategory={setSelectedModalCategory}
                  setInModalCategory={setInModalCategory}
                />
              )))}
            </ReactSortable>
          </div>
          <AnimatePresence>
            {inModalLevel && <ModalLevel Action={modalAction} selectedCategory={selectedModalCategory} selectedLevel={selectedModalLevel} closeModal={() => { setInModalLevel(false); invalidateDashboardGridContent(); fetchLevels(); }} />}
          </AnimatePresence>
          <AnimatePresence>
            {inModalCategory && <ModalCategory Action={modalCategoryAction} selectedCategory={selectedModalCategory} closeModal={() => { setInModalCategory(false); invalidateDashboardGridContent(); fetchCategories(); }} />}
          </AnimatePresence>
          <AnimatePresence>
            {inModalManageAdmins && <ManageUsers view="admins" closeModal={() => setInModalManageAdmins(false)} />}
          </AnimatePresence>
          <AnimatePresence>
            {inModalViewSuperadmins && <ManageUsers view="superadmins" closeModal={() => setInModalViewSuperadmins(false)} />}
          </AnimatePresence>
          <AnimatePresence>
            {inModalSendAnnouncement && <AnnouncementModal closeModal={() => setInModalSendAnnouncement(false)} />}
          </AnimatePresence>
          <AnimatePresence>
            {inModalManageReports && <ManageReports closeModal={() => setInModalManageReports(false)} />}
          </AnimatePresence>
        </>
      }
    </>
  );
}

export default DashboardGrid;
